import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Lock, CreditCard, AlertCircle } from 'lucide-react';
import { API_URL, money, paxLabel, sliceLabel, servicesFromSelection, extrasTotals } from './duffelShared';
import { NonRefundableNotice, FinalBookingConfirmation } from '../terms/NonRefundableNotice';
import { buildTermsAcceptance } from '../terms/nonRefundableTerms';
import { PassportValidityNotice } from '../booking/PassportValidityNotice';
import { PassengerCard as PaxShell, PassengerFields, ContactFields } from '../booking/PassengerFields';
import { travellerError, passportAcknowledgement, sixMonthsAfter } from '../../utils/travellerValidation';

const TITLES = { adult: ['Mr', 'Mrs', 'Ms', 'Miss'], child: ['Mstr', 'Miss'], infant_without_seat: ['Mstr', 'Miss'] };
const toType = (t) => (t === 'infant_without_seat' ? 'INFANT' : t === 'child' ? 'CHILD' : 'ADULT');
const genderFromTitle = (t) => (['Mrs', 'Ms', 'Miss'].includes(t) ? 'Female' : 'Male');

const PassengerCard = ({ pax, index, all, form, update, docsRequired, minExpiry }) => (
  <PaxShell index={index} total={all.length} type={toType(pax.type)} lead={index === 0} label={paxLabel(pax, all)} testId={`duffel-passenger-${index}`}>
    <PassengerFields form={form} update={update} titles={TITLES[pax.type] || TITLES.adult} passengerType={toType(pax.type)} docsRequired={docsRequired} testIdPrefix={`duffel-passenger-${index}`} minExpiry={minExpiry} />
  </PaxShell>
);

export const DuffelCheckout = ({ flight, fares, combo, extras, sel, total }) => {
  const [forms, setForms] = useState(() => extras.passengers.map((p) => ({ title: (TITLES[p.type] || TITLES.adult)[0], first_name: '', last_name: '', date_of_birth: '', passport_number: '', passport_expiry: '' })));
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [terms, setTerms] = useState(false);
  const [finalAccepted, setFinalAccepted] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [noticeAcceptedAt, setNoticeAcceptedAt] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const services = servicesFromSelection(extras, sel);
  const totals = extrasTotals(extras, sel);
  const update = (i, k, v) => setForms((f) => f.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const [passportOk, setPassportOk] = useState(false);
  const departDate = (fares.slices?.[0]?.departing_at || '').slice(0, 10) || null;
  const returnDate = (fares.slices?.[fares.slices.length - 1]?.departing_at || '').slice(0, 10) || departDate;
  const passportBlocked = forms.some((f) => f.passport_expiry && departDate && f.passport_expiry < sixMonthsAfter(departDate));

  const validate = () => {
    for (let i = 0; i < forms.length; i++) {
      const f = forms[i];
      const who = paxLabel(extras.passengers[i], extras.passengers);
      if (!f.title || !f.first_name.trim() || !f.last_name.trim() || !f.date_of_birth) return `${who}: please complete title, name and date of birth`;
      if (extras.identity_documents_required && (!f.passport_number || !f.passport_expiry)) return `${who}: passport number and expiry are required by the airline`;
      if (f.passport_number && !/^[A-Z0-9]{5,15}$/.test(f.passport_number)) return `${who}: passport number should be 5–15 letters/numbers`;
      const te = travellerError({ ...f, type: toType(extras.passengers[i].type) }, who, departDate, returnDate);
      if (te) return te;
    }
    if (passportBlocked) return 'A traveller\u2019s passport has less than six months\u2019 validity on the date of travel \u2014 it must be renewed before booking';
    if (!passportOk) return 'Please confirm you have checked every traveller\u2019s passport validity';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email)) return 'Please enter a valid contact email';
    if ((contact.phone || '').replace(/\D/g, '').length < 10) return 'Please enter a valid phone number (include area code)';
    if (!terms) return 'Please accept the booking terms to continue';
    if (!noticeAccepted) return 'Please confirm you have read the Non-Refundable Fare Notice';
    if (!finalAccepted) return 'Please tick the Final Booking Confirmation to continue';
    return null;
  };

  const pay = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setSubmitting(true);
    const counts = { adults: 0, children: 0, infants: 0, youth: 0 };
    extras.passengers.forEach((p) => { counts[p.type === 'infant_without_seat' ? 'infants' : p.type === 'child' ? 'children' : 'adults'] += 1; });
    try {
      const { data } = await axios.post(`${API_URL}/api/flights/payment/start`, {
        origin_url: window.location.origin,
        terms_acceptance: { ...buildTermsAcceptance({ fare_brands: combo.brands, change: combo.change, refund: combo.refund, baggage: combo.baggage, extras: services.map((x) => x.label) }, noticeAcceptedAt), ...passportAcknowledgement(passportOk) },
        flight_id: flight.id,
        flight_data: { ...flight, source: 'duffel', price: total, fare_brands: combo.brands, duffel_offer_id: combo.offer_id },
        passengers: forms.map((f, i) => ({
          type: toType(extras.passengers[i].type), title: f.title, first_name: f.first_name.trim(), last_name: f.last_name.trim(),
          date_of_birth: f.date_of_birth, gender: genderFromTitle(f.title), nationality: 'GB',
          passport_number: f.passport_number || null, passport_expiry: f.passport_expiry || null, email: contact.email, phone: contact.phone,
        })),
        contact: { email: contact.email.trim(), phone: contact.phone.trim() },
        passenger_counts: counts,
        total_price: total,
        currency: 'GBP',
        duffel: { offer_id: combo.offer_id, offer_request_id: fares.offer_request_id, services: services.map(({ id, quantity, amount, label, type }) => ({ id, quantity, amount, label, type })) },
      });
      sessionStorage.setItem('duffelPendingRef', data.ref);
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d && typeof d === 'object' ? [d.message, ...(d.errors || [])].filter(Boolean).join(' \u2022 ') : d || 'Unable to start payment. Please try again or call 01908 220000.');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="duffel-checkout-step">
      {extras.passengers.map((p, i) => <PassengerCard key={p.id} pax={p} index={i} all={extras.passengers} form={forms[i]} update={(k, v) => update(i, k, v)} docsRequired={extras.identity_documents_required} minExpiry={departDate || undefined} />)}

      <Card className="rounded-xl border border-slate-200 shadow-sm" data-testid="duffel-contact-card">
        <CardContent className="p-4 sm:p-6">
          <ContactFields email={contact.email} phone={contact.phone} onEmail={(v) => setContact((c) => ({ ...c, email: v }))} onPhone={(v) => setContact((c) => ({ ...c, phone: v }))} testIdPrefix="duffel-contact" />
        </CardContent>
      </Card>

      <Card className="border-2 border-orange-200" data-testid="duffel-price-breakdown">
        <CardContent className="p-5 space-y-2 text-sm">
          <div className="font-bold text-slate-900 text-base mb-2">Price breakdown</div>
          {fares.slices.map((sl, i) => (
            <div key={sl.index} className="flex justify-between"><span className="text-slate-600">{sliceLabel(i)} {sl.origin}→{sl.destination} · <strong>{combo.brands[i]}</strong></span>{i === 0 && <span data-testid="breakdown-fare-total">{money(combo.total_amount)}</span>}</div>
          ))}
          <div className="text-xs text-slate-500 -mt-1">Fare total for {extras.passengers.length} passenger{extras.passengers.length !== 1 ? 's' : ''}, all taxes included</div>
          {services.map((s, i) => <div key={`${s.id}-${i}`} className="flex justify-between"><span className="text-slate-600">{s.label}{s.passenger_id ? ` · ${paxLabel(extras.passengers.find((p) => p.id === s.passenger_id) || extras.passengers[0], extras.passengers)}` : ''}</span><span>{money(s.amount * s.quantity)}</span></div>)}
          {services.length > 0 && <div className="flex justify-between text-slate-500"><span>Extras subtotal</span><span>{money(totals.total)}</span></div>}
          <div className="flex justify-between items-baseline border-t border-slate-200 pt-3 mt-2"><span className="font-bold text-slate-900">Total to pay</span><span className="text-2xl font-black text-slate-900" data-testid="breakdown-grand-total">{money(total)}</span></div>
        </CardContent>
      </Card>

      <Card data-testid="duffel-terms-card">
        <CardContent className="p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="duffel-terms-checkbox" className="mt-0.5" />
            <span className="text-sm text-slate-700">I confirm all passenger names match their passports and I accept Flight380's booking terms and the airline's fare conditions. Low-cost carrier bookings are issued by the airline and follow its own change, refund and baggage rules.</span>
          </label>
          <PassportValidityNotice checked={passportOk} onChange={setPassportOk} blocked={passportBlocked} testIdPrefix="duffel-passport" />
          <NonRefundableNotice accepted={noticeAccepted} onChange={(v) => { setNoticeAccepted(v); setNoticeAcceptedAt(v ? new Date().toISOString() : null); }} />
          <FinalBookingConfirmation accepted={finalAccepted} onChange={setFinalAccepted} />
          {error && <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="duffel-checkout-error"><AlertCircle className="h-4 w-4 mt-0.5" />{error}</div>}
          <Button onClick={pay} disabled={submitting || !terms || !finalAccepted || !noticeAccepted} className="w-full h-14 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold text-lg disabled:opacity-60" data-testid="duffel-pay-btn">
            {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}
            {submitting ? 'Redirecting to secure payment…' : `Pay ${money(total)} & Confirm Booking`}
          </Button>
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" />Card payment by Revolut · Your ticket is issued by the airline only after payment succeeds</div>
        </CardContent>
      </Card>
    </div>
  );
};
